import { useEffect, useRef, useState } from 'react'

/** Resize the containing grid, including when the review is portalled into a task rail. */
export function ReviewResizeHandle() {
  const handle = useRef<HTMLDivElement>(null)
  const layout = useRef<HTMLElement | null>(null)
  const drag = useRef<{ x: number; width: number } | null>(null)
  const [size, setSize] = useState({ width: 320, max: 640 })

  useEffect(() => {
    const grid = handle.current?.closest<HTMLElement>('.peer-review-layout.with-decision, .review-task-layout')
    if (!grid) return
    layout.current = grid
    const observer = new ResizeObserver(() => {
      const available = grid.getBoundingClientRect().width
      if (!available) return
      setSize({
        width: Math.round(handle.current?.parentElement?.getBoundingClientRect().width ?? 320),
        max: Math.max(320, Math.floor(available * 0.6)),
      })
    })
    observer.observe(grid)
    return () => {
      observer.disconnect()
      grid.style.removeProperty('--review-sidebar-width')
      layout.current = null
    }
  }, [])

  const resize = (value: number) => {
    const width = Math.round(Math.max(320, Math.min(size.max, value)))
    layout.current?.style.setProperty('--review-sidebar-width', `${width}px`)
    setSize(current => ({ ...current, width }))
  }

  return <div
    ref={handle}
    className="review-sidebar-resizer"
    role="separator"
    aria-label="Resize review sidebar"
    aria-orientation="vertical"
    aria-valuemin={320}
    aria-valuemax={size.max}
    aria-valuenow={Math.min(size.max, size.width)}
    tabIndex={0}
    title="Drag to resize the review sidebar"
    onPointerDown={event => {
      if (event.button !== 0) return
      event.preventDefault()
      drag.current = { x: event.clientX, width: event.currentTarget.parentElement!.getBoundingClientRect().width }
      event.currentTarget.setPointerCapture(event.pointerId)
    }}
    onPointerMove={event => {
      if (drag.current) resize(drag.current.width + drag.current.x - event.clientX)
    }}
    onPointerUp={event => {
      drag.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
      event.currentTarget.blur()
    }}
    onPointerCancel={() => { drag.current = null }}
    onLostPointerCapture={() => { drag.current = null }}
    onKeyDown={event => {
      const delta = event.key === 'ArrowLeft' ? 24 : event.key === 'ArrowRight' ? -24 : 0
      if (!delta) return
      event.preventDefault()
      resize(size.width + delta)
    }}
  />
}
